import { gql, useMutation, useQuery } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { useMemo, useState } from 'react'

import {
  ArtistMembershipPaymentEditQuery,
  ArtistMembershipPaymentEditQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  CreatePaymentMethod,
  CreatePaymentMethodMutation,
  CreatePaymentMethodMutationVariables,
  DeletePaymentSource,
  DeletePaymentSourceMutation,
  DeletePaymentSourceMutationVariables,
  DowngradeSubscriptionPlan,
  DowngradeSubscriptionPlanMutation,
  DowngradeSubscriptionPlanMutationVariables,
  MembershipInterval,
  PaymentDetailFragmentDoc,
  PaymentMethod,
  SubscriptionDetailFragmentDoc,
  UpgradeSubscriptionPlan,
  UpgradeSubscriptionPlanMutation,
  UpgradeSubscriptionPlanMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { sleep } from '@/lib/sleep'
import { confirmCardSetupWithStripe, createTokenWithCard } from '@/lib/stripe'

import type { PaymentFormValues } from '../PaymentCreate/template/PaymentCreateForm'
import { MembershipPlan } from './template/ArtistMembershipPaymentEditForm'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { router, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const { showMessage, showError } = useMessage()
  const { t } = useTranslation('artistMembershipPaymentEdit')

  const { data, loading, refetch } = useQuery<
    ArtistMembershipPaymentEditQuery,
    ArtistMembershipPaymentEditQueryVariables
  >(QUERY, {
    variables: {
      id:
        config.applicationType === 'standalone'
          ? config.artistId
          : router.query.artistId,
    },
  })

  const [createPaymentMethod, { loading: submittingCreatePayment }] =
    useMutation<
      CreatePaymentMethodMutation,
      CreatePaymentMethodMutationVariables
    >(CreatePaymentMethod, {
      onCompleted() {
        refetch()
      },
    })
  const [deletePaymentSource] = useMutation<
    DeletePaymentSourceMutation,
    DeletePaymentSourceMutationVariables
  >(DeletePaymentSource)

  const [upgradeSubscriptionPlan, { loading: upgradingSubscriptionPlan }] =
    useMutation<
      UpgradeSubscriptionPlanMutation,
      UpgradeSubscriptionPlanMutationVariables
    >(UpgradeSubscriptionPlan, {
      async onCompleted() {
        showMessage(t('updateSuccess'))
        router.goBack(routerPages.MembershipManagement)
      },
    })

  const [downgradeSubscriptionPlan, { loading: downgradingSubscriptionPlan }] =
    useMutation<
      DowngradeSubscriptionPlanMutation,
      DowngradeSubscriptionPlanMutationVariables
    >(DowngradeSubscriptionPlan, {
      async onCompleted() {
        showMessage(t('updateSuccess'))
        router.goBack(routerPages.MembershipManagement)
      },
    })

  const plans = useMemo<MembershipPlan[]>(() => {
    if (!data?.artist?.subscription?.products?.length) {
      return []
    }

    const product = data?.artist?.subscription?.products.find(
      ({ productId }) => router.query.productId === productId
    )
    if (!product || !product.plans?.length) {
      return []
    }

    return orderBy(
      product.plans.map(plan => ({
        interval: plan.interval,
        amount: plan.amount,
        formattedAmount: plan.formattedAmount ?? '0',
        annualDiscountText: product.annualDiscountText ?? '',
        planId: plan.planId,
      })),
      ['interval'],
      ['desc']
    )
  }, [data?.artist?.subscription?.products, router.query.productId])

  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  return {
    state: {
      loading,
      artistId:
        config.applicationType === 'standalone'
          ? config.artistId
          : router.query.artistId,
      paymentSource: data?.user?.credit ?? null,
      plans,
      patron: data?.artist?.patron,
      submitting: upgradingSubscriptionPlan || downgradingSubscriptionPlan,
      submittingCreatePayment,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      /**
       * NOTE:
       * 年間/月額の支払い方法変更APIが現在ないため、現在可能な支払い方法変更は
       * 年間コンビニ → 年間クレカ へのアップグレードのみである
       * https://app.clickup.com/t/908127/UT-15353
       */
      async onSubmit(values: { interval: MembershipInterval }) {
        const plan = plans.find(plan => plan.interval === values.interval)

        if (!plan?.amount || !data?.artist?.patron?.amount) {
          return
        }

        if (plan?.amount > data?.artist?.patron?.amount) {
          await upgradeSubscriptionPlan({
            variables: {
              planId: plan?.planId ?? '',
              artistId: data?.artist?.id ?? '',
              paymentMethod: PaymentMethod.CreditCard,
            },
          }).catch(error => {
            switch (error.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                return
              /**
               * @description
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                return
              /**
               * @description
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                return
              default:
                showError(error)
                break
            }
          })
          return
        }
        await downgradeSubscriptionPlan({
          variables: {
            planId: plan?.planId ?? '',
            artistId: data?.artist?.id ?? '',
            paymentMethod: PaymentMethod.CreditCard,
          },
        }).catch(showError)
      },
      async onSubmitCreatePayment(values: PaymentFormValues) {
        const [expMonth, expYear] = values.expirationDate.split('/')
        const token = await createTokenWithCard({
          number: values.cardNumber.replace(/\s/g, ''),
          name: values.cardHolder,
          cvc: values.securityCode.replace(/\s/g, ''),
          expMonth: expMonth,
          expYear: expYear,
        }).catch(error => {
          throw new Error(t(error.message))
        })
        /**
         * @description
         * トークンを元にクレジットカード登録を
         * 決済プロバイダーにリクエストする
         * 3DSecure が求められなかった場合 CreditCard 型を、
         * 3DSecure が求められた場合 RequiresAction 型を
         * value に持つデータが取得される
         */
        const { data: paymentResponse } = await createPaymentMethod({
          variables: { createPaymentMethodToken: token },
        })
        /**
         * @description
         * 3DSecure が求められた場合
         * すなわち RequiresAction 型のデータが取得された場合の処理
         */
        if (
          paymentResponse &&
          'clientSecret' in paymentResponse.createPaymentMethod
        ) {
          const { clientSecret } = paymentResponse.createPaymentMethod
          /**
           * @description
           * ここでモーダルが起動される
           */
          const result = await confirmCardSetupWithStripe(clientSecret)
          if (result.error) throw new Error(result.error.message)
        }
        /**
         * @description
         * webhook通知〜クレジットカード登録の完了を待つため
         * 暫定的に sleep を加えている
         */
        await sleep(2000)
        await refetch()
      },
      async onSubmitDeletePayment() {
        await deletePaymentSource().then(() => showMessage(t('deleteSuccess')))
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
}

const QUERY = gql`
  query ArtistMembershipPaymentEdit($id: ID) {
    artist(id: $id) {
      id
      ...ArtistMembershipSubscriptionDetail
      patron {
        id
        amount
        paymentMethod
        subscription {
          ...SubscriptionDetail
        }
      }
    }
    user {
      id
      credit {
        ...PaymentDetail
      }
    }
  }
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${PaymentDetailFragmentDoc}
  ${SubscriptionDetailFragmentDoc}
`
